import React, { ReactNode, useMemo, forwardRef } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  Grid,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaStateField from 'components/inputs/AuturaStateField';
import AuturaDateTimePicker from 'components/inputs/AuturaDateTimePicker';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { cardHeaderSx, cardSx, mobileCardSx } from 'utils';
import { DoNotTow, DisplayMode, FormMode } from 'types';
import { ROUTES } from 'router/routes';
import { TransitionProps } from '@mui/material/transitions';
import { Save, Cancel } from '@mui/icons-material';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

interface DNTDialogProps {
  open: boolean;
  handleCancel: () => void;
  handleSubmit: () => void;
  dnt: DoNotTow;
  setDNT: (dnt: DoNotTow) => void;
  formMode: FormMode;
}

export const DNTDialog = ({
  dnt,
  handleSubmit,
  open,
  handleCancel,
  setDNT,
  formMode,
}: DNTDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints, palette } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case 'donottow.plate.number':
        setDNT({
          ...dnt,
          plate: {
            state: dnt?.plate?.state || '',
            number: value,
          },
        });
        break;
      case 'donottow.vin':
        setDNT({ ...dnt, vin: value });
        break;
      case 'donottow.reason':
        setDNT({ ...dnt, reason: value });
        break;
    }
  };

  const title = useMemo(() => {
    if (formMode === 'create') {
      return t('buttons.create', {
        name: t('donottow.label', { ns: 'entities' }),
      });
    }
    return t('buttons.update', {
      name: t('donottow.label', { ns: 'entities' }),
    });
  }, [formMode]);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      aria-label={title}
      fullScreen={mobile}
      maxWidth={'sm'}
      sx={{ mt: { xs: -1, lg: 0 } }}
    >
      <ValidatorForm onSubmit={handleSubmit}>
        <Card raised sx={mobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              mobile
                ? {
                    display: 'flex',
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={'h4'}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: 'left',
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, flexGrow: 1 }}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <AuturaTextField
                  name={'donottow.plate.number'}
                  label={t('donottow.plate.number', { ns: 'entities' })}
                  value={dnt?.plate?.number.toUpperCase() || ''}
                  onChange={handleChange}
                  required
                  readonly={formMode === 'edit'}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <AuturaStateField
                  stateCode={dnt?.plate?.state || ''}
                  setStateCode={(stateCode: string) => {
                    setDNT({
                      ...dnt,
                      plate: {
                        number: dnt?.plate?.number || '',
                        state: stateCode,
                      },
                    });
                  }}
                  required
                  label={t('donottow.plate.state', { ns: 'entities' })}
                  readonly={formMode === 'edit'}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <AuturaDateTimePicker
                  label={t('donottow.startTime', { ns: 'entities' })}
                  required
                  disablePast
                  dateTime={
                    dnt.startTime ? new Date(dnt.startTime) : new Date()
                  }
                  setDateTime={(date: Date | null) => {
                    setDNT({
                      ...dnt,
                      startTime: date ? date : new Date(),
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <AuturaDateTimePicker
                  label={t('donottow.endTime', { ns: 'entities' })}
                  dateTime={dnt.endTime ? new Date(dnt.endTime) : null}
                  disablePast
                  setDateTime={(date: Date | null) => {
                    setDNT({
                      ...dnt,
                      endTime: date ? date : '',
                    });
                  }}
                />
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  name={'donottow.reason'}
                  label={t('donottow.reason', { ns: 'entities' })}
                  value={dnt.reason}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={12}>
                <Stack
                  width='100%'
                  direction='row'
                  spacing={2}
                  justifyContent='flex-end'
                  mt={2}
                  display='flex'
                  alignItems='flex-end'
                  alignContent='flex-end'
                >
                  <Button
                    variant={'outlined'}
                    onClick={handleCancel}
                    color={'secondary'}
                    endIcon={<Cancel />}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant={'contained'}
                    type='submit'
                    color={'secondary'}
                    endIcon={<Save />}
                  >
                    {t('submit')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};

export const getParentRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_DO_NOT_TOW;
    case 'group':
      return ROUTES.PROPERTY_GROUP_DO_NOT_TOW;
    default:
      return ROUTES.PROPERTY_DO_NOT_TOW;
  }
};
